import React, { useContext, useEffect, useState } from 'react';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import { makeStyles, createStyles, withStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Switch from '@material-ui/core/Switch';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';


import ReplayRoundedIcon from '@material-ui/icons/ReplayRounded';

import TooltipButton from '../shared/TooltipButton';
import { AppContext } from '../App';
import Tr from '../shared/tr';

import IncreaseIcon from '../../assets/image/toolpanel/increase.svg';
import DecreaseIcon from '../../assets/image/toolpanel/decrease.svg';
import ResetIcon from '../../assets/image/toolpanel/reset.svg';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      height: '100%',
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    grid: {
      width: '100%',
      margin: '20px 20px 0 0 ',
    },
    btn: {
      margin: 'auto',
      textTransform: 'none',
    },
  })
);

const BlueSwitch = withStyles({
  switchBase: {
    color: '#36B0C9',
    '&$checked': {
      color: '#36B0C9',
    },
    '&$checked + $track': {
      backgroundColor: '#36B0C9',
    },
  },
  checked: {},
  track: {},
})(Switch);

const OrientationAdjustment = () => {
  const {
    occlusalPlaneActor,
    normalMeshes,
    selectedMesh,
    rotationStep,
    setRotationStep,
    movementStep,
    setMovementStep,
    rotationX,
    setRotationX,
    rotationY,
    setRotationY,
    rotationZ,
    setRotationZ,
    movementLowerZ,
    setMovementLowerZ,
    movementUpperZ,
    setMovementUpperZ,
    occlusalPlaneVisible,
    setOcclusalPlaneVisible,
    setRefreshCanvas,
  } = useContext(AppContext);

  const classes = useStyles();
  const tr = new Tr();
  const [movementDisplayValue, setMovementDisplayValue] = useState(0.0);

  useEffect(() => {
    let mesh = selectedMesh;
    if (mesh !== null && mesh.isUpperJaw()) {
      setMovementDisplayValue(movementUpperZ);
      return;
    }
    if (mesh !== null && mesh.isLowerJaw()) {
      setMovementDisplayValue(movementLowerZ);
      return;
    }
    setMovementDisplayValue(0);
  }, [movementUpperZ, movementLowerZ, selectedMesh]);

  useEffect(() => {
    normalMeshes.forEach((mesh) => {
      mesh.rotateX(rotationX);
    });
    setRefreshCanvas(true);
  }, [rotationX]);

  useEffect(() => {
    normalMeshes.forEach((mesh) => {
      mesh.rotateY(rotationY);
    });
    setRefreshCanvas(true);
  }, [rotationY]);

  useEffect(() => {
    normalMeshes.forEach((mesh) => {
      mesh.rotateZ(rotationZ);
    });
    setRefreshCanvas(true);
  }, [rotationZ]);

  useEffect(() => {
    normalMeshes.forEach((mesh) => {
      if (mesh.isLowerJaw()) {
        mesh.doMovement(movementLowerZ);
      }
    });
    setRefreshCanvas(true);
  }, [movementLowerZ]);

  useEffect(() => {
    normalMeshes.forEach((mesh) => {
      if (mesh.isUpperJaw()) {
        mesh.doMovement(movementUpperZ);
      }
    });
    setRefreshCanvas(true);
  }, [movementUpperZ]);

  const checkRotateChange = (changeValue: number) => {
    let value = formatNumber(changeValue, 1);
    if (value > 90) {
      value = 90;
    } else if (value < -90) {
      value = -90;
    }
    return value;
  };

  const checkMovementZChange = (changeValue: number) => {
    let value = formatNumber(changeValue, 2);
    if (value > 5) {
      value = 5;
    } else if (value < -5) {
      value = -5;
    }
    return value;
  };

  const handleRotationStepChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setRotationStep(event.target.value as number);
  };

  const handleMovementStepChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setMovementStep(event.target.value as number);
  };

  const handlePlaneVisibleChange = (event) => {
    setOcclusalPlaneVisible(event.target.checked);
  };
  useEffect(() => {
    occlusalPlaneActor.setVisibility(occlusalPlaneVisible);
    setRefreshCanvas(true);
  }, [occlusalPlaneVisible]);

  function formatNumber(value: number, n: number) {
    return Math.round(value * Math.pow(10, n)) / Math.pow(10, n);
  }

  const onResetAll = () => {
    setMovementUpperZ(0.0);
    setMovementLowerZ(0.0);
    setRotationX(0.0);
    setRotationY(0.0);
    setRotationZ(0.0);
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={2} alignItems="center" justify="center" className={classes.grid}>
        <Grid item xs={12}>
          <Typography gutterBottom variant="h5">
            <b>{tr.titlebarOrientationAdjustment()}</b>
          </Typography>
        </Grid>

        <Grid item container xs={12} alignItems="center">
          <Grid item xs={10}>
            <Typography gutterBottom variant="subtitle1">
              {tr.showOcclusionPlane()}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <BlueSwitch checked={occlusalPlaneVisible} onChange={handlePlaneVisibleChange}></BlueSwitch>
          </Grid>
        </Grid>

        <Grid item container xs={12} alignItems="center">
          <Grid item xs={10}>
            <Typography gutterBottom variant="subtitle1">
              {tr.rotationStep()}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Select
              labelId="demo-simple-select-filled-label"
              id="demo-simple-select-filled"
              value={rotationStep}
              onChange={handleRotationStepChange}
            >
              <MenuItem value={0.5}>
                <em>0.5</em>
              </MenuItem>
              <MenuItem value={1.0}>1.0</MenuItem>
              <MenuItem value={5.0}>5.0</MenuItem>
            </Select>
          </Grid>
        </Grid>

        <Grid item container xs={12} alignItems="center">
          <Grid item xs={2}>
            <Typography gutterBottom variant="subtitle1">
              X
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <TextField
              id="filled-read-only-input"
              label={tr.angle()}
              value={rotationX}
              InputProps={{
                readOnly: true,
              }}
              variant="filled"
            />
          </Grid>
          <Grid item xs={2}>
            <TooltipButton
              title={tr.decrease()}
              imgSrc={DecreaseIcon}
              onClick={() => {
                setRotationX(checkRotateChange(rotationX - rotationStep));
              }}
            ></TooltipButton>
          </Grid>
          <Grid item xs={2}>
            <TooltipButton
              title={tr.increase()}
              imgSrc={IncreaseIcon}
              onClick={() => {
                setRotationX(checkRotateChange(rotationX + rotationStep));
              }}
            ></TooltipButton>
          </Grid>
          <Grid item xs={2}>
            <TooltipButton
              title="Reset"
              imgSrc={ResetIcon}
              onClick={() => {
                setRotationX(0.0);
              }}
            ></TooltipButton>
          </Grid>
        </Grid>

        <Grid item container xs={12} alignItems="center">
          <Grid item xs={2}>
            <Typography gutterBottom variant="subtitle1">
              Y
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <TextField
              id="filled-read-only-input"
              label={tr.angle()}
              value={rotationY}
              InputProps={{
                readOnly: true,
              }}
              variant="filled"
            />
          </Grid>
          <Grid item xs={2}>
            <TooltipButton
              title={tr.decrease()}
              imgSrc={DecreaseIcon}
              onClick={() => {
                setRotationY(checkRotateChange(rotationY - rotationStep));
              }}
            ></TooltipButton>
          </Grid>
          <Grid item xs={2}>
            <TooltipButton
              title={tr.increase()}
              imgSrc={IncreaseIcon}
              onClick={() => {
                setRotationY(checkRotateChange(rotationY + rotationStep));
              }}
            ></TooltipButton>
          </Grid>
          <Grid item xs={2}>
            <TooltipButton
              title="Reset"
              imgSrc={ResetIcon}
              onClick={() => {
                setRotationY(0.0);
              }}
            ></TooltipButton>
          </Grid>
        </Grid>

        <Grid item container xs={12} alignItems="center">
          <Grid item xs={2}>
            <Typography gutterBottom variant="subtitle1">
              Z
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <TextField
              id="filled-read-only-input"
              label={tr.angle()}
              value={rotationZ}
              InputProps={{
                readOnly: true,
              }}
              variant="filled"
            />
          </Grid>
          <Grid item xs={2}>
            <TooltipButton
              title={tr.decrease()}
              imgSrc={DecreaseIcon}
              onClick={() => {
                setRotationZ(checkRotateChange(rotationZ - rotationStep));
              }}
            ></TooltipButton>
          </Grid>
          <Grid item xs={2}>
            <TooltipButton
              title={tr.increase()}
              imgSrc={IncreaseIcon}
              onClick={() => {
                setRotationZ(checkRotateChange(rotationZ + rotationStep));
              }}
            ></TooltipButton>
          </Grid>
          <Grid item xs={2}>
            <TooltipButton
              title="Reset"
              imgSrc={ResetIcon}
              onClick={() => {
                setRotationZ(0.0);
              }}
            ></TooltipButton>
          </Grid>
        </Grid>

        <Grid item container xs={12} alignItems="center">
          <Grid item xs={10}>
            <Typography gutterBottom variant="subtitle1">
              {tr.movementStep()}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Select
              labelId="demo-simple-select-filled-label"
              id="demo-simple-select-filled"
              value={movementStep}
              onChange={handleMovementStepChange}
            >
              <MenuItem value={0.01}>
                <em>0.01</em>
              </MenuItem>
              <MenuItem value={0.05}>0.05</MenuItem>
              <MenuItem value={0.1}>0.1</MenuItem>
            </Select>
          </Grid>
        </Grid>

        <Grid item container xs={12} alignItems="center">
          <Grid item xs={2}>
            <Typography gutterBottom variant="subtitle1">
              Z
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <TextField
              id="filled-read-only-input"
              label={tr.distance()}
              value={movementDisplayValue}
              InputProps={{
                readOnly: true,
              }}
              variant="filled"
            />
          </Grid>
          <Grid item xs={2}>
            <TooltipButton
              title={tr.decrease()}
              imgSrc={DecreaseIcon}
              // visible= {isUpperSelected()}
              onClick={() => {
                let mesh = selectedMesh;
                if (mesh !== null && mesh.isUpperJaw()) {
                  setMovementUpperZ(checkMovementZChange(movementUpperZ - movementStep));
                }
                if (mesh !== null && mesh.isLowerJaw()) {
                  setMovementLowerZ(checkMovementZChange(movementLowerZ - movementStep));
                }
              }}
            ></TooltipButton>
          </Grid>
          <Grid item xs={2}>
            <TooltipButton
              title={tr.increase()}
              imgSrc={IncreaseIcon}
              onClick={() => {
                let mesh = selectedMesh;
                if (mesh != null && mesh.isUpperJaw()) {
                  setMovementUpperZ(checkMovementZChange(movementUpperZ + movementStep));
                }
                if (mesh !== null && mesh.isLowerJaw()) {
                  setMovementLowerZ(checkMovementZChange(movementLowerZ + movementStep));
                }
              }}
            ></TooltipButton>
          </Grid>
          <Grid item xs={2}>
            <TooltipButton
              title={tr.reset()}
              imgSrc={ResetIcon}
              onClick={() => {
                let mesh = selectedMesh;
                if (mesh != null && mesh.isUpperJaw()) {
                  setMovementUpperZ(0);
                }
                if (mesh !== null && mesh.isLowerJaw()) {
                  setMovementLowerZ(0);
                }
              }}
            ></TooltipButton>
          </Grid>
        </Grid>

        <Grid item container xs={12} alignItems="center">
          <Button className={classes.btn} variant="outlined" onClick={onResetAll} startIcon={<ReplayRoundedIcon />}>
            {tr.orientationAdjustmentResetAll()}
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

export default OrientationAdjustment;
